import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    Grid,
    TextField,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Typography,
    Divider,
    MenuItem,
    Link,
    Box,
    Button as MuiButton,
    Snackbar,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Tooltip,
    Collapse,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { Store as StoreIcon } from "components/MaterialSymbol/MaterialIcons";
import { FileCopy as FileCopyIcon } from "components/MaterialSymbol/MaterialIcons";
import { CheckCircleOutline as CheckCircleOutlineIcon } from "components/MaterialSymbol/MaterialIcons";
import { EmojiObjects as EmojiObjectsIcon } from "components/MaterialSymbol/MaterialIcons";
import Modal from "components/Modal/Modal";
import Button from "components/CustomButtons/Button.js";
import MercadoLibreService from "services/MercadoLibreService";
import { useConfirmationDialog } from "hooks/useConfirmationDialog";
import useDealerSwr from "hooks/useDealerSwr";
import useMercadoLibreCategories from "hooks/useMercadoLibreCategories";

const validationSchema = Yup.object({
    ml_app_id: Yup.string().required("App ID is required"),
    ml_client_secret: Yup.string().required("Client Secret is required"),
    ml_redirect_uri: Yup.string().url("Must be a valid URL"),
    ml_category_id: Yup.string().nullable(),
    ml_currency_id: Yup.string().nullable(),
    ml_buying_mode: Yup.string().nullable(),
    ml_condition: Yup.string().nullable(),
    ml_listing_type_id: Yup.string().nullable(),
    ml_shipping_mode: Yup.string().nullable(),
});

const useStyles = makeStyles((theme) => ({
    mlButton: {
        backgroundColor: "#FFE600",
        color: "#2D3277",
        fontWeight: "bold",
        "&:hover": {
            backgroundColor: "#E5CE00",
        },
    },
    disabledSection: {
        opacity: 0.5,
        pointerEvents: "none",
        userSelect: "none",
    },
    pulseIcon: {
        marginRight: 8,
        color: "#FBC02D",
        verticalAlign: "middle",
        fontSize: "24px",
        display: "inline-block", // Required for transform
    },
    animatedPulse: {
        animation: "$pulse 2s infinite ease-in-out",
    },
    guidanceHeader: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        padding: "16px",
        "&:hover": {
            backgroundColor: "#f9f9f9",
        },
        transition: "background-color 0.2s ease-in-out",
        borderRadius: "8px 8px 0 0",
    },
    "@keyframes pulse": {
        "0%": {
            transform: "scale(1)",
            filter: "brightness(1) drop-shadow(0 0 0px rgba(251, 192, 45, 0))",
        },
        "50%": {
            transform: "scale(1.4)",
            filter: "brightness(1.5) drop-shadow(0 0 10px rgba(251, 192, 45, 0.8))",
        },
        "100%": {
            transform: "scale(1)",
            filter: "brightness(1) drop-shadow(0 0 0px rgba(251, 192, 45, 0))",
        },
    },
}));

const ConfigureMLModal = ({ open, onClose, onSuccess, showWarning = false }) => {
    const classes = useStyles();
    const { dealer } = useDealerSwr();
    const { openDialog } = useConfirmationDialog();
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showGuidance, setShowGuidance] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);
    const handleReset = () => setActiveStep(0);
    const [internalShowWarning, setInternalShowWarning] = useState(false);
    const mlConnected = !!dealer?.mlConnected;
    const { categories, isLoading: isCategoriesLoading } = useMercadoLibreCategories(null, mlConnected);

    const [initialValues, setInitialValues] = useState({
        ml_app_id: "",
        ml_client_secret: "",
        ml_redirect_uri: "",
        ml_category_id: "",
        ml_currency_id: "USD",
        ml_buying_mode: "buy_it_now",
        ml_condition: "new",
        ml_listing_type_id: "gold_special",
        ml_shipping_mode: "me2",
        ml_shipping_local_pick_up: false,
        ml_shipping_free_shipping: false,
    });

    useEffect(() => {
        if (dealer) {
            setInitialValues({
                ml_app_id: dealer.mlAppId || "",
                ml_client_secret: dealer.mlClientSecret || "",
                ml_redirect_uri: dealer.mlRedirectUri || "",
                ml_category_id: dealer.mlCategoryId || "MPE1747",
                ml_currency_id: dealer.mlCurrencyId || "USD",
                ml_buying_mode: dealer.mlBuyingMode || "buy_it_now",
                ml_condition: dealer.mlCondition || "new",
                ml_listing_type_id: dealer.mlListingTypeId || "gold_special",
                ml_shipping_mode: dealer.mlShippingMode || "me2",
                ml_shipping_local_pick_up: !!dealer.mlShippingLocalPickUp,
                ml_shipping_free_shipping: !!dealer.mlShippingFreeShipping,
            });

            if (showWarning && !dealer.mlConnected) {
                setInternalShowWarning(true);
            }
        }
    }, [dealer, showWarning]);

    useEffect(() => {
        if (open) {
            setInternalShowWarning(showWarning);
        }
    }, [open, showWarning]);

    const handleConnect = async () => {
        if (!dealer?.id) return;
        setConnecting(true);
        try {
            // Save config first to ensure App ID is up to date
            await MercadoLibreService.updateConfig(dealer.id, formik.values);

            // Redirect Uri from the same page
            const redirectUri = window.location.origin + window.location.pathname;
            const response = await MercadoLibreService.generateAuthUrl(
                dealer.id,
                redirectUri,
                formik.values.ml_app_id,
            );

            if (response && response.url) {
                window.location.href = response.url;
            }
        } catch (error) {
            console.error("Failed to generate auth url", error);
            openDialog({
                title: "Error",
                message: "Failed to connect to MercadoLibre. Please check credentials.",
                showActions: true,
                onConfirm: () => { },
            });
            setConnecting(false);
        }
    };

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (!dealer?.id) return;

            setLoading(true);
            try {
                const response = await MercadoLibreService.updateConfig(dealer.id, values);
                onSuccess && onSuccess();
                onClose();
            } catch (error) {
                console.error("Failed to save config", error);
                openDialog({
                    title: "Error",
                    message: "Failed to save configuration. Please try again.",
                    showActions: true,
                    onConfirm: () => { },
                });
            } finally {
                setLoading(false);
            }
        },
    });

    const redirectUri = window.location.origin + window.location.pathname;

    const handleCopyRedirectUri = () => {
        navigator.clipboard.writeText(redirectUri);
        setSnackbar({ open: true, message: "Redirect URI copied!" });
    };

    return (
        <Modal open={open} modalTitle="Configure MercadoLibre" handleClose={onClose} maxWidth="md">
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    {internalShowWarning && (
                        <Grid item xs={12}>
                            <Alert severity="warning" onClose={() => setInternalShowWarning(false)}>
                                Before generating an export, you need to configure your MercadoLibre integration.
                            </Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Typography variant="h6">Credentials</Typography>
                        <Divider />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="ml_app_id"
                            name="ml_app_id"
                            label="App ID"
                            value={formik.values.ml_app_id}
                            onChange={formik.handleChange}
                            error={formik.touched.ml_app_id && Boolean(formik.errors.ml_app_id)}
                            helperText={formik.touched.ml_app_id && formik.errors.ml_app_id}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id="ml_client_secret"
                            name="ml_client_secret"
                            label="Client Secret"
                            type="password"
                            value={formik.values.ml_client_secret}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.ml_client_secret &&
                                Boolean(formik.errors.ml_client_secret)
                            }
                            helperText={
                                formik.touched.ml_client_secret && formik.errors.ml_client_secret
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <MuiButton
                            variant="contained"
                            type="button"
                            className={classes.mlButton}
                            onClick={handleConnect}
                            disabled={
                                connecting ||
                                !formik.values.ml_app_id ||
                                !formik.values.ml_client_secret
                            }
                            startIcon={<StoreIcon />}
                            fullWidth
                        >
                            {connecting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                "Validate / Connect MercadoLibre"
                            )}
                        </MuiButton>
                    </Grid>

                    {/* App Creation Guidance Section */}
                    <Grid item xs={12}>
                        <Box bgcolor="#fff" borderRadius={8} border="1px solid #e0e0e0" boxShadow={1} overflow="hidden">
                            <Tooltip title={showGuidance ? "" : "Click here to show the steps"} arrow placement="top">
                                <Box
                                    className={classes.guidanceHeader}
                                    onClick={() => setShowGuidance(!showGuidance)}
                                >
                                    <EmojiObjectsIcon
                                        className={`${classes.pulseIcon} ${(!mlConnected && !showGuidance) ? classes.animatedPulse : ""}`}
                                    />
                                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#2D3277', flexGrow: 1 }}>
                                        How to create your MercadoLibre App
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {showGuidance ? "Click to hide" : "Click to show steps"}
                                    </Typography>
                                </Box>
                            </Tooltip>

                            <Collapse in={showGuidance}>
                                <Divider />
                                <Box p={1}>
                                    <Stepper activeStep={activeStep} orientation="vertical" style={{ padding: '0 16px 16px 16px' }}>
                                        {/* Step 1 */}
                                        <Step>
                                            <StepLabel><Typography style={{ fontWeight: 'bold' }}>Access MercadoLibre Developer Center</Typography></StepLabel>
                                            <StepContent>
                                                <Typography variant="body2" gutterBottom>
                                                    Go to the following URL and log in with your MercadoLibre account:
                                                </Typography>
                                                <Link href="https://developers.mercadolibre.com.pe/devcenter" target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                                                    https://developers.mercadolibre.com.pe/devcenter
                                                </Link>
                                                <Box mt={2}>
                                                    <MuiButton variant="contained" color="primary" size="small" onClick={handleNext}>Continue</MuiButton>
                                                </Box>
                                            </StepContent>
                                        </Step>

                                        {/* Step 2 */}
                                        <Step>
                                            <StepLabel><Typography style={{ fontWeight: 'bold' }}>Create a New Application</Typography></StepLabel>
                                            <StepContent>
                                                <Typography variant="body2">
                                                    Create a new application and complete the required information (application name, logo, description, etc.).
                                                </Typography>
                                                <Box mt={2}>
                                                    <MuiButton size="small" onClick={handleBack} style={{ marginRight: 8 }}>Back</MuiButton>
                                                    <MuiButton variant="contained" color="primary" size="small" onClick={handleNext}>Continue</MuiButton>
                                                </Box>
                                            </StepContent>
                                        </Step>

                                        {/* Step 3 */}
                                        <Step>
                                            <StepLabel><Typography style={{ fontWeight: 'bold' }}>Configure Redirect URI</Typography></StepLabel>
                                            <StepContent>
                                                <Typography variant="body2" gutterBottom>
                                                    Copy this value and paste it into the <b>Redirect URIs</b> field in MercadoLibre:
                                                </Typography>
                                                <Box display="flex" alignItems="center" mt={1}>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={redirectUri}
                                                        InputProps={{
                                                            readOnly: true,
                                                            style: { backgroundColor: '#f5f5f5', fontSize: '0.8rem', fontFamily: 'monospace' }
                                                        }}
                                                    />
                                                    <MuiButton
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={handleCopyRedirectUri}
                                                        style={{ marginLeft: 8, minWidth: '40px' }}
                                                        title="Copy to clipboard"
                                                    >
                                                        <FileCopyIcon fontSize="small" />
                                                    </MuiButton>
                                                </Box>
                                                <Box mt={2}>
                                                    <MuiButton size="small" onClick={handleBack} style={{ marginRight: 8 }}>Back</MuiButton>
                                                    <MuiButton variant="contained" color="primary" size="small" onClick={handleNext}>Continue</MuiButton>
                                                </Box>
                                            </StepContent>
                                        </Step>

                                        {/* Step 4 */}
                                        <Step>
                                            <StepLabel><Typography style={{ fontWeight: 'bold' }}>Enable OAuth Flows</Typography></StepLabel>
                                            <StepContent>
                                                <Typography variant="body2" component="div">
                                                    In the OAuth Flows section:
                                                    <Box mt={1}>
                                                        <Typography variant="caption" display="block">• Enable <b>Refresh Token</b></Typography>
                                                        <Typography variant="caption" display="block">• Leave <b>Authorization Code</b> and <b>Client Credentials</b> enabled (active by default)</Typography>
                                                    </Box>
                                                </Typography>
                                                <Box mt={2}>
                                                    <MuiButton size="small" onClick={handleBack} style={{ marginRight: 8 }}>Back</MuiButton>
                                                    <MuiButton variant="contained" color="primary" size="small" onClick={handleNext}>Continue</MuiButton>
                                                </Box>
                                            </StepContent>
                                        </Step>

                                        {/* Step 5 */}
                                        <Step>
                                            <StepLabel><Typography style={{ fontWeight: 'bold' }}>Configure Business Type</Typography></StepLabel>
                                            <StepContent>
                                                <Typography variant="body2">
                                                    In the <b>Business (Negocios)</b> section, enable <b>MercadoLibre</b>.
                                                </Typography>
                                                <Box mt={2}>
                                                    <MuiButton size="small" onClick={handleBack} style={{ marginRight: 8 }}>Back</MuiButton>
                                                    <MuiButton variant="contained" color="primary" size="small" onClick={handleNext}>Continue</MuiButton>
                                                </Box>
                                            </StepContent>
                                        </Step>

                                        {/* Step 6 */}
                                        <Step>
                                            <StepLabel><Typography style={{ fontWeight: 'bold' }}>Set Permissions</Typography></StepLabel>
                                            <StepContent>
                                                <Typography variant="body2">
                                                    In <b>Permissions → Publication and Synchronization</b>, select:
                                                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#2D3277', marginTop: 4 }}>
                                                        Read and Write
                                                    </Typography>
                                                </Typography>
                                                <Box mt={2}>
                                                    <MuiButton size="small" onClick={handleBack} style={{ marginRight: 8 }}>Back</MuiButton>
                                                    <MuiButton variant="contained" color="primary" size="small" onClick={handleNext}>Finish</MuiButton>
                                                </Box>
                                            </StepContent>
                                        </Step>
                                    </Stepper>

                                    {activeStep === 6 && (
                                        <Box p={3} textAlign="center">
                                            <Typography variant="body2" gutterBottom>All steps completed!</Typography>
                                            <MuiButton size="small" color="primary" onClick={handleReset}>Review steps again</MuiButton>
                                        </Box>
                                    )}

                                    <Divider />
                                    <Box p={2} textAlign="center">
                                        <Typography variant="caption" display="block" color="textSecondary" gutterBottom>
                                            For more details, check the official documentation:
                                        </Typography>
                                        <Link
                                            href="https://developers.mercadolibre.com.pe/es_ar/crea-una-aplicacion-en-mercado-libre-es"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            color="secondary"
                                            variant="caption"
                                            style={{ fontWeight: 'bold' }}
                                        >
                                            Official ML App Creation Guide
                                        </Link>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </Grid>

                    <Grid item xs={12} style={{ display: 'none' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" style={{ marginTop: 10 }}>
                                Export Settings
                            </Typography>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (mlConnected) setShowAdvanced(!showAdvanced);
                                }}
                                disabled={!mlConnected}
                                style={{ marginTop: 10, opacity: !mlConnected ? 0.5 : 1 }}
                            >
                                {showAdvanced
                                    ? "Hide Advanced export settings"
                                    : "Advanced export settings"}
                            </Link>
                        </Box>
                        <Divider />
                        {!mlConnected ? (
                            <Box mt={1}>
                                <Typography variant="caption" color="textSecondary">
                                    Validate MercadoLibre credentials to enable export settings.
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="caption" color="textSecondary">
                                These settings define how products are published to MercadoLibre.
                            </Typography>
                        )}
                    </Grid>

                    <Box
                        component={Grid}
                        container
                        item
                        xs={12}
                        spacing={3}
                        className={!mlConnected ? classes.disabledSection : ""}
                        style={{ display: 'none' }}
                    >
                        {showAdvanced && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        id="ml_currency_id"
                                        name="ml_currency_id"
                                        label="Currency"
                                        select
                                        value={formik.values.ml_currency_id}
                                        onChange={formik.handleChange}
                                    >
                                        <MenuItem value="USD">USD (US Dollar)</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        id="ml_buying_mode"
                                        name="ml_buying_mode"
                                        label="Buying Mode"
                                        select
                                        value={formik.values.ml_buying_mode}
                                        onChange={formik.handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem value="buy_it_now">Buy it now</MenuItem>
                                        <MenuItem value="auction">Auction</MenuItem>
                                        <MenuItem value="classified">Classified</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        id="ml_condition"
                                        name="ml_condition"
                                        label="Condition"
                                        select
                                        value={formik.values.ml_condition}
                                        onChange={formik.handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem value="new">New</MenuItem>
                                        <MenuItem value="used">Used</MenuItem>
                                        <MenuItem value="not_specified">Not Specified</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        id="ml_listing_type_id"
                                        name="ml_listing_type_id"
                                        label="Listing Type"
                                        select
                                        value={formik.values.ml_listing_type_id}
                                        onChange={formik.handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem value="gold_pro">Gold Pro</MenuItem>
                                        <MenuItem value="gold_special">Gold Special</MenuItem>
                                        <MenuItem value="gold_premium">Gold Premium</MenuItem>
                                        <MenuItem value="free">Free</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        id="ml_shipping_mode"
                                        name="ml_shipping_mode"
                                        label="Shipping Mode"
                                        select
                                        value={formik.values.ml_shipping_mode}
                                        onChange={formik.handleChange}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem value="me2">MercadoEnvíos 2</MenuItem>
                                        <MenuItem value="me1">MercadoEnvíos 1</MenuItem>
                                        <MenuItem value="not_specified">Not Specified</MenuItem>
                                        <MenuItem value="custom">Custom</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        id="ml_category_id"
                                        name="ml_category_id"
                                        label="Default Category"
                                        select
                                        disabled={isCategoriesLoading}
                                        value={formik.values.ml_category_id}
                                        onChange={formik.handleChange}
                                        helperText={
                                            isCategoriesLoading ? "Loading categories..." : ""
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formik.values.ml_shipping_local_pick_up}
                                                onChange={formik.handleChange}
                                                name="ml_shipping_local_pick_up"
                                                color="primary"
                                            />
                                        }
                                        label="Local Pick Up"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formik.values.ml_shipping_free_shipping}
                                                onChange={formik.handleChange}
                                                name="ml_shipping_free_shipping"
                                                color="primary"
                                            />
                                        }
                                        label="Free Shipping"
                                    />
                                </Grid>
                            </>
                        )}
                    </Box>

                    <Grid
                        item
                        xs={12}
                        style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}
                    >
                        <Button color="transparent" onClick={onClose} style={{ marginRight: 10 }}>
                            Cancel
                        </Button>
                        <Button type="submit" color="rose" disabled={loading || !mlConnected}>
                            {loading ? <CircularProgress size={24} /> : "Save and close"}
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Modal>
    );
};

export default ConfigureMLModal;
